import { TrashIcon, XIcon } from 'lucide-react';
import { HistoryGroupedSlider } from '~/components/atoms/HistoryGroupedSlider';
import { Button } from '~/components/ui/button';
import { useStore } from '~/store';
import { REGION_COLOR } from './colors';

/** Popover body listing every keyed region with hover-sync, delete, and a
 *  "Clear all" action. Mounted from the matte toolbar's region button. */
export function RegionListPanel() {
  const regions = useStore((s) => s.background.regions);
  const selectedId = useStore((s) => s.editor.selectedRegionId);
  const wandTolerance = useStore((s) => s.background.wandTolerance);
  const removeRegion = useStore((s) => s.removeRegion);
  const clearRegions = useStore((s) => s.clearRegions);
  const setHoveredRegionId = useStore((s) => s.setHoveredRegionId);
  const setSelectedRegionId = useStore((s) => s.setSelectedRegionId);
  const setWandTolerance = useStore((s) => s.setWandTolerance);
  const setRegionTolerance = useStore((s) => s.setRegionTolerance);

  return (
    <div className="space-y-3 text-xs">
      <div className="space-y-1.5">
        <span className="font-medium text-muted-foreground">Tool</span>
        <div className="grid grid-cols-[max-content_minmax(0,1fr)_max-content] items-center gap-x-2">
          <span>Wand tolerance</span>
          <HistoryGroupedSlider
            aria-label="Wand tolerance"
            value={[wandTolerance]}
            min={0}
            max={255}
            step={1}
            onValueChange={([v]) => v != null && setWandTolerance(v)}
          />
          <span className="w-7 text-right tabular-nums text-muted-foreground">{wandTolerance}</span>
        </div>
      </div>
      {regions.length === 0 ? (
        <p className="text-muted-foreground">No keyed regions yet.</p>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {regions.length} region{regions.length === 1 ? '' : 's'} ·{' '}
              {regions.reduce((acc, r) => acc + r.pixelCount, 0)} px
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-6 px-2"
              onClick={() => {
                if (window.confirm(`Clear ${regions.length} keyed region(s)?`)) clearRegions();
              }}
            >
              <TrashIcon className="size-3" />
              Clear all
            </Button>
          </div>
          <ul className="max-h-60 space-y-0.5 overflow-y-auto">
            {regions.map((r) => {
              const selected = r.id === selectedId;
              const isFlood = r.tolerance > 0;
              return (
                <li
                  key={r.id}
                  className={`rounded px-1.5 py-1 hover:bg-accent ${selected ? 'bg-accent' : ''}`}
                  onMouseEnter={() => setHoveredRegionId(r.id)}
                  onMouseLeave={() => setHoveredRegionId(null)}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ background: REGION_COLOR }}
                    />
                    <button
                      type="button"
                      className="flex-1 truncate text-left tabular-nums"
                      onClick={() => setSelectedRegionId(r.id)}
                    >
                      {r.pixelCount} px · {r.bbox.x},{r.bbox.y} → {r.bbox.x + r.bbox.w},
                      {r.bbox.y + r.bbox.h}
                    </button>
                    <button
                      type="button"
                      aria-label="Remove region"
                      className="rounded p-0.5 text-muted-foreground hover:bg-destructive/20 hover:text-destructive"
                      onClick={() => removeRegion(r.id)}
                    >
                      <XIcon className="size-3" />
                    </button>
                  </div>
                  {isFlood && (
                    <div className="mt-1 grid grid-cols-[max-content_minmax(0,1fr)_max-content] items-center gap-x-2 pl-5">
                      <span className="text-muted-foreground">Tol</span>
                      <HistoryGroupedSlider
                        aria-label="Region tolerance"
                        value={[r.tolerance]}
                        min={1}
                        max={255}
                        step={1}
                        onValueChange={([v]) => v != null && setRegionTolerance(r.id, v)}
                      />
                      <span className="w-7 text-right tabular-nums text-muted-foreground">
                        {r.tolerance}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
